"use client";

import React, { useState } from "react";
import { useAtom, useAtomValue, useSetAtom } from "jotai";
import { SiteList } from "@/components";
import { Input, Badge, Toggle, Button } from "@oreme/ui";
import { SiteType } from "@/types";
import {
  navigationAtom,
  sidebarVisibilityAtom,
  siteSidebarSearchValueAtom,
  siteSidebarSearchVisibilityAtom,
  missionAtom,
} from "@/store";
import { useMapLibre } from "@/hooks";
import { ListFilter, X } from "lucide-react";
import { filterSitesByMission } from "@/utils";
import { useTranslations } from "next-intl";

type SiteSidebarProps = {
  data: SiteType[];
};

const filterData = (sites: SiteType[], search: string) => {
  return sites.filter((site) => {
    return site.site.toLowerCase().includes(search.toLowerCase());
  });
};

function SiteSidebar({ data }: Readonly<SiteSidebarProps>) {
  const t = useTranslations("site.SiteSidebar");
  const { map } = useMapLibre();
  const mission = useAtomValue(missionAtom);
  const toggleSidebarVisibility = useSetAtom(sidebarVisibilityAtom);
  const setNavigation = useSetAtom(navigationAtom);

  let sites = data;
  if (mission) sites = filterSitesByMission(sites, mission);

  const [searchValue, setSearchValue] = useAtom(siteSidebarSearchValueAtom);
  const [isSearchVisible, toggleSearchVisibility] = useAtom(
    siteSidebarSearchVisibilityAtom,
  );

  const [filteredData, setFilteredData] = useState(
    filterData(sites, searchValue),
  );

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSearchValue(value);
    setFilteredData(filterData(data, value));
  };

  const handleClose = () => {
    toggleSidebarVisibility();
    setNavigation(null);
    map!.resize();
  };

  const handleClear = () => {
    setSearchValue("");
    setFilteredData(filterData(data, ""));
  };

  return (
    <div className="flex-1 flex flex-col w-full h-full">
      <header className="flex flex-row items-center justify-start pl-4 pr-2 h-12 rounded-none m-0 gap-3">
        <h3 className="text-base flex flex-row gap-3 items-center">
          <span>{t("title")}</span>
          <Badge variant="outline">{data.length}</Badge>
        </h3>
        <div className="flex justify-center items-center ml-auto gap-1">
          <Toggle
            className="p-0 m-0"
            title={t("filter")}
            aria-label={t("filter")}
            pressed={isSearchVisible}
            onPressedChange={toggleSearchVisibility}
            size="xs"
          >
            <ListFilter size={16} />
          </Toggle>
          <Button
            className="border-0 size-6"
            size="icon"
            onClick={() => {
              handleClose();
            }}
            variant="ghost"
            title={t("close")}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </header>
      <form
        className={`relative flex-row justify-between items-center px-4 py-3 border-t ${isSearchVisible ? "flex" : "hidden"}`}
        onSubmit={(e) => e.preventDefault()}
      >
        <Input
          className="text-xs pr-8"
          placeholder={t("search")}
          onChange={handleSearch}
          value={searchValue}
        />
        {searchValue.length > 0 ? (
          <Button
            size="icon"
            onClick={() => {
              handleClear();
            }}
            variant="link"
            title={t("clear")}
            className="absolute right-5"
          >
            <X className="h-3 w-3" />
          </Button>
        ) : null}
      </form>
      <div className="relative flex-1 overflow-hidden border-t">
        <SiteList data={filteredData} />
      </div>
    </div>
  );
}

export { SiteSidebar };
